import React from 'react';
import Layout from '../components/Layout';

const Privacy = () => {
  return (
    <Layout>
      <section style={{ padding: '4rem 2rem', maxWidth: '800px', margin: '0 auto', lineHeight: '1.6' }}>
        <h1 style={{ textAlign: 'left', color: '#333', marginBottom: '0.5rem' }}>Privacy Policy for Tonometer Scan</h1>
        <p style={{ textAlign: 'left', color: '#666', marginBottom: '2rem' }}>Last Updated: May 17, 2026</p>
        
        <div style={{ textAlign: 'left' }}>
          <h2 style={{ color: '#333', marginTop: '2rem', marginBottom: '1rem' }}>1. Introduction</h2>
          <p style={{ marginBottom: '1rem' }}>
            Tonometer Scan ("we," "our," or "us") is committed to protecting your privacy. Your trust is important to us, and this Privacy Policy explains how we handle your information when you use our mobile application (the "App").
          </p>
          <p style={{ marginBottom: '1rem' }}>
            Our core principle is to collect as little data as possible and to perform all sensitive operations directly on your device.
          </p>
          
          <h2 style={{ color: '#333', marginTop: '2rem', marginBottom: '1rem' }}>2. Information We Collect</h2>
          <p style={{ marginBottom: '1rem' }}>
            We have designed the App to function without collecting or storing your personal health information on our servers.
          </p>
          
          <h3 style={{ color: '#333', marginTop: '1.5rem', marginBottom: '0.5rem' }}>2.1 Information You Provide to the App</h3>
          <p style={{ marginBottom: '0.5rem' }}>
            <strong>Camera Access:</strong> The App requires access to your camera to provide the live scanning feature. The video stream is processed in real time on your device and is never transmitted or stored.
          </p>
          <p style={{ marginBottom: '0.5rem' }}>
            <strong>Photos from Your Library:</strong> If you choose to select a photo, the App receives temporary, one-time access to that specific photo for processing.
          </p>
          <p style={{ marginBottom: '0.5rem' }}>
            <strong>User-Initiated Feedback Reports:</strong>
          </p>
          <p style={{ marginBottom: '0.5rem', paddingLeft: '1rem' }}>
            If you choose to report a recognition issue, the App can prepare an email that may include:
          </p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '2rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>A cropped image of your tonometer screen (only the measurement area, never your face or surroundings)</li>
            <li style={{ marginBottom: '0.5rem' }}>Technical details such as device model, iOS version, app version, and recognition confidence</li>
            <li style={{ marginBottom: '0.5rem' }}>Any text you type in the message body</li>
          </ul>
          <p style={{ marginBottom: '1rem' }}>
            This report is optional and fully controlled by you. You can review and edit all information before sending the email. Nothing is transmitted automatically.
          </p>
          
          <h3 style={{ color: '#333', marginTop: '1.5rem', marginBottom: '0.5rem' }}>2.2 Information the App Generates</h3>
          <p style={{ marginBottom: '0.5rem' }}>
            <strong>Health Data:</strong> The App extracts blood pressure (systolic and diastolic) and heart-rate readings from the camera feed or photo you provide. This data is held temporarily on your device until you choose to save or discard it.
          </p>
          <p style={{ marginBottom: '0.5rem' }}>
            <strong>Technical Metadata for Reports:</strong> When you create a report, the App may include contextual metadata (e.g., lighting conditions, confidence scores) to help us analyze recognition accuracy. This information remains local unless you manually send it.
          </p>
          
          <h3 style={{ color: '#333', marginTop: '1.5rem', marginBottom: '0.5rem' }}>2.3 Anonymous Analytics Data</h3>
          <p style={{ marginBottom: '0.5rem' }}>
            We collect completely anonymous, aggregated usage data to help us improve the App.
          </p>
          <p style={{ marginBottom: '0.5rem' }}>
            This includes events like "scan_initiated" or "health_data_saved."
          </p>
          <p style={{ marginBottom: '1rem' }}>
            This data is not linked to you or your device and contains no personal or health information.
          </p>

          <h3 style={{ color: '#333', marginTop: '1.5rem', marginBottom: '0.5rem' }}>2.4 Website Analytics Data</h3>
          <p style={{ marginBottom: '0.5rem' }}>
            On our website, we may use Google Analytics and Microsoft Clarity to understand page visits, navigation patterns, and general site performance.
          </p>
          <p style={{ marginBottom: '1rem' }}>
            These website analytics tools load only after you allow analytics in the website consent prompt. They are separate from the App and do not receive your blood-pressure readings, camera images, or Apple Health data.
          </p>
          
          <h2 style={{ color: '#333', marginTop: '2rem', marginBottom: '1rem' }}>3. How We Use Your Information</h2>
          <p style={{ marginBottom: '1rem' }}>
            Your privacy is paramount. All processing of sensitive information happens on your device.
          </p>
          
          <h3 style={{ color: '#333', marginTop: '1.5rem', marginBottom: '0.5rem' }}>3.1 Camera Feed and Photos</h3>
          <p style={{ marginBottom: '0.5rem' }}>
            The camera feed and any photos you select are processed locally on your device by Apple's Vision framework to extract the numerical readings.
          </p>
          <p style={{ marginBottom: '1rem' }}>
            We do not store, view, or transmit your photos or video stream. They are processed in real time and immediately discarded after the readings are extracted.
          </p>
          
          <h3 style={{ color: '#333', marginTop: '1.5rem', marginBottom: '0.5rem' }}>3.2 Health Data</h3>
          <p style={{ marginBottom: '0.5rem' }}>
            The extracted blood-pressure and heart-rate readings are only stored in the Apple Health app on your device, and only when you explicitly tap the button to save them.
          </p>
          <p style={{ marginBottom: '1rem' }}>
            We do not maintain any database, and we do not store your health readings on our servers.
          </p>
          
          <h3 style={{ color: '#333', marginTop: '1.5rem', marginBottom: '0.5rem' }}>3.3 User-Initiated Reports</h3>
          <p style={{ marginBottom: '0.5rem' }}>
            If you choose to send a report email, we use the information you include solely to:
          </p>
          <ul style={{ marginBottom: '0.5rem', paddingLeft: '1.5rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>Analyze recognition errors and improve OCR accuracy</li>
            <li style={{ marginBottom: '0.5rem' }}>Identify device-specific or lighting-related issues</li>
            <li style={{ marginBottom: '0.5rem' }}>Enhance the performance and usability of future versions of the App</li>
          </ul>
          <p style={{ marginBottom: '0.5rem' }}>
            Reports are handled as support emails and are not linked to your account or identity unless you include personal details yourself.
          </p>
          <p style={{ marginBottom: '1rem' }}>
            Cropped screenshots and technical data are deleted from our inbox within 90 days of receipt.
          </p>
          
          <h3 style={{ color: '#333', marginTop: '1.5rem', marginBottom: '0.5rem' }}>3.4 Anonymous Analytics</h3>
          <p style={{ marginBottom: '0.5rem' }}>
            Anonymous usage data is used only to understand which features are popular, identify bugs, and improve overall experience.
          </p>
          <p style={{ marginBottom: '1rem' }}>
            It cannot be used to identify you.
          </p>

          <h3 style={{ color: '#333', marginTop: '1.5rem', marginBottom: '0.5rem' }}>3.5 Website Analytics</h3>
          <p style={{ marginBottom: '1rem' }}>
            Website analytics are used to understand how visitors use this website, improve navigation, and evaluate whether site content is helpful. You can change your analytics preference from the website footer.
          </p>
          
          <h2 style={{ color: '#333', marginTop: '2rem', marginBottom: '1rem' }}>4. Data Storage and Security</h2>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>We do not maintain any database of user health information.</li>
            <li style={{ marginBottom: '0.5rem' }}>All of your health data is stored securely within the Apple Health ecosystem on your device, under your control.</li>
            <li style={{ marginBottom: '0.5rem' }}>All image and video processing occurs locally on your device.</li>
            <li style={{ marginBottom: '0.5rem' }}>Reports you send are stored temporarily in our secure email system (Google Workspace / Gmail) and automatically purged after the retention period.</li>
          </ul>
          
          <h2 style={{ color: '#333', marginTop: '2rem', marginBottom: '1rem' }}>5. Third-Party Services</h2>
          <p style={{ marginBottom: '1rem' }}>
            We use a minimal number of trusted third-party services to provide the App's functionality:
          </p>
          <p style={{ marginBottom: '0.5rem' }}>
            <strong>Apple (StoreKit & HealthKit):</strong> For securely saving data to Apple Health and managing in-app subscriptions.
          </p>
          <p style={{ marginBottom: '0.5rem' }}>
            <strong>TelemetryDeck:</strong> For privacy-focused, anonymous analytics.
          </p>
          <p style={{ marginBottom: '0.5rem' }}>
            <strong>Google Analytics:</strong> For consent-based website traffic analytics.
          </p>
          <p style={{ marginBottom: '0.5rem' }}>
            <strong>Microsoft Clarity:</strong> For consent-based website usage analytics and session insights.
          </p>
          <p style={{ marginBottom: '1rem' }}>
            <strong>Google Mail (Gmail):</strong> For receiving optional user-initiated feedback reports by email. These messages are transmitted securely using standard email encryption (TLS) and stored in Google's secure infrastructure.
          </p>
          
          <h2 style={{ color: '#333', marginTop: '2rem', marginBottom: '1rem' }}>6. Your Rights and Control</h2>
          <p style={{ marginBottom: '1rem' }}>
            You are in complete control of your data. You have the right to:
          </p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>Access, manage, and delete all your blood-pressure and heart-rate data directly within the Apple Health app.</li>
            <li style={{ marginBottom: '0.5rem' }}>Control the App's permissions (for Camera and Health) at any time in your device's Settings.</li>
            <li style={{ marginBottom: '0.5rem' }}>Choose whether or not to send a report and review its contents before sending.</li>
            <li style={{ marginBottom: '0.5rem' }}>Request deletion of any feedback email you've sent by contacting us at support@tonometer-scan.app.</li>
          </ul>
          
          <h2 style={{ color: '#333', marginTop: '2rem', marginBottom: '1rem' }}>7. Legal Basis (EU/EEA Users)</h2>
          <p style={{ marginBottom: '0.5rem' }}>
            For users located in the European Union or European Economic Area:
          </p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>Processing of your health data within Apple Health is under your explicit consent.</li>
            <li style={{ marginBottom: '0.5rem' }}>Anonymous analytics are processed under legitimate interest (Article 6(1)(f) GDPR).</li>
            <li style={{ marginBottom: '0.5rem' }}>Processing of user-initiated reports (including optional images) is based on your explicit consent (Article 6(1)(a) GDPR). You may withdraw this consent at any time.</li>
          </ul>
          
          <h2 style={{ color: '#333', marginTop: '2rem', marginBottom: '1rem' }}>8. Children's Privacy</h2>
          <p style={{ marginBottom: '1rem' }}>
            Our App is not intended for children under the age of 13. We do not knowingly collect information from children.
          </p>
          
          <h2 style={{ color: '#333', marginTop: '2rem', marginBottom: '1rem' }}>9. Changes to This Privacy Policy</h2>
          <p style={{ marginBottom: '1rem' }}>
            We may update this Privacy Policy from time to time.
          </p>
          <p style={{ marginBottom: '1rem' }}>
            Significant changes will be displayed in the App's settings and on our website.
          </p>
          
          <h2 style={{ color: '#333', marginTop: '2rem', marginBottom: '1rem' }}>10. Contact Us</h2>
          <p style={{ marginBottom: '1rem' }}>
            If you have any questions, requests, or concerns regarding this Privacy Policy, please contact us at:
          </p>
          <p style={{ marginBottom: '1rem' }}>
            📧 <a href="mailto:support@tonometer-scan.app" style={{ color: '#4285f4', textDecoration: 'none' }}>support@tonometer-scan.app</a>
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default Privacy;
